import { makeCoverSvg } from "~/constants/templates/exampleTemplate";

/**
 * Checks if the active object is a drop target.
 * If it is, it resets its src to the placeholder image instead of deleting the layer.
 * If it is not, it removes the object from the canvas entirely.
 */
export const clearDropTargetOrDelete = (editor: any, activeObject: any) => {
    if (!activeObject || !editor) return;

    // If it's a drop target, we just want to clear the dropped image
    // and restore it to its placeholder state.
    if (activeObject.metadata?.isDropTarget) {
        // ─── Snapshot ALL bounds BEFORE calling setSrc ───────────────────────
        // setSrc is async: Fabric mutates width/height after loading the new
        // image's natural dimensions. Capturing these NOW (in the outer scope)
        // guarantees the callback closure always has the correct original values.
        const originalScaleX = activeObject.scaleX || 1;
        const originalScaleY = activeObject.scaleY || 1;
        // Actual rendered pixel size of the drop-target zone
        const originalWidth = activeObject.width * originalScaleX;
        const originalHeight = activeObject.height * originalScaleY;
        const originalLeft = activeObject.left;
        const originalTop = activeObject.top;
        // ─────────────────────────────────────────────────────────────────────

        // For inner pages: restore a plain grey rectangle — no icon or text,
        // matching the initial placeholder appearance.
        const makeGreySvg = (w: number, h: number): string => {
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><rect width="${w}" height="${h}" fill="#d8d8d8"/></svg>`;
            return `data:image/svg+xml;base64,${btoa(svg)}`;
        };

        let placeholderSrc = makeGreySvg(originalWidth, originalHeight);

        // For labelled cover regions use the descriptive icon placeholder
        if (activeObject.metadata?.coverRegion === "back") {
            placeholderSrc = makeCoverSvg("Back Cover", originalWidth, originalHeight);
        } else if (activeObject.metadata?.coverRegion === "front") {
            placeholderSrc = makeCoverSvg("Front Cover", originalWidth, originalHeight);
        }

        // Fabric JS requires setSrc for images so it can load the image first
        activeObject.setSrc(placeholderSrc, () => {
            // Reset preview and originalImageUrl metadata
            activeObject.set("metadata", {
                ...activeObject.metadata,
                preview: placeholderSrc,
                originalImageUrl: undefined
            });

            // Restore the original rendered bounds that were captured BEFORE
            // setSrc was called (Fabric mutates the object dimensions while loading).
            activeObject.set({
                width: originalWidth,
                height: originalHeight,
                left: originalLeft,
                top: originalTop,
                scaleX: 1,
                scaleY: 1,
                cropX: 0,
                cropY: 0,
            });
            activeObject.setCoords();
            // Force re-render after src has loaded
            editor.canvas?.requestRenderAll();
            editor.history?.save();

            setTimeout(() => {
                editor.canvas?.requestRenderAll();
            }, 100);
        }, { crossOrigin: 'anonymous' });
    } else {
        // Normal layers (text, shapes, normal images) get deleted natively
        editor.objects.remove();
        editor.canvas?.requestRenderAll()
        editor.history?.save()
    }
};
